<template>
    <div id="app" class="site container hfeed">
        <HeadNav selectedLanguage="en" page="features-multi-account" />

        <div class="markdown-content">
            <h1>GB WhatsApp Multi-Account Features – Complete Guide</h1>

            <p>Managing multiple WhatsApp accounts has never been easier. GB WhatsApp allows users to run multiple accounts simultaneously on a single device, eliminating the need for multiple phones or complex workarounds. This guide covers all the multi-account features available in GB WhatsApp.</p>

            <h2>Introduction to Multi-Account Management</h2>

            <p>GB WhatsApp's multi-account feature is designed for users who need to manage both personal and business communications, or those with multiple phone numbers. This feature provides a seamless experience without compromising security or functionality.</p>

            <h2>Account Switching</h2>

            <p>Quickly switch between multiple accounts:</p>
            <ul>
                <li><strong>One-Tap Switch:</strong> Change accounts with a single tap</li>
                <li><strong>Quick Switcher:</strong> Access all accounts from notification bar</li>
                <li><strong>Account Lock:</strong> Secure individual accounts with PIN</li>
                <li><strong>Profile Pictures:</strong> Use different profile pictures for each account</li>
                <li><strong>Status Updates:</strong> Set different statuses for each account</li>
            </ul>

            <h2>Notification Separation</h2>

            <p>Keep your communications organized with separate notifications:</p>
            <ul>
                <li><strong>Custom Sounds:</strong> Set unique notification sounds per account</li>
                <li><strong>LED Colors:</strong> Different LED notification colors for each account</li>
                <li><strong>Vibration Patterns:</strong> Custom vibration patterns for identification</li>
                <li><strong>Badge Counter:</strong> Show unread count for all accounts</li>
                <li><strong>Do Not Disturb:</strong> Silence specific accounts while allowing others</li>
            </ul>

            <h2>Account Linking</h2>

            <p>Manage account connections efficiently:</p>
            <ul>
                <li><strong>Easy Setup:</strong> Link additional accounts in seconds</li>
                <li><strong>QR Code Linking:</strong> Simple account verification process</li>
                <li><strong>Number Verification:</strong> Easy verification via SMS or call</li>
                <li><strong>Account Recovery:</strong> Recover accounts with phone number verification</li>
            </ul>

            <h2>Data Isolation</h2>

            <p>Keep your data secure and separate:</p>
            <ul>
                <li><strong>Separate Databases:</strong> Each account has its own independent database</li>
                <li><strong>Storage Separation:</strong> Media files stored separately per account</li>
                <li><strong>Chat History:</strong> Complete separation of chat histories</li>
                <li><strong>Settings Independence:</strong> Custom settings for each account</li>
            </ul>

            <h2>Risk Warnings</h2>

            <p>Important considerations for multi-account users:</p>
            <ul>
                <li><strong>Resource Usage:</strong> Multiple accounts may consume more battery and storage</li>
                <li><strong>Account Ban Risk:</strong> Using multiple accounts may increase detection risk</li>
                <li><strong>Data Backup:</strong> Each account needs separate backup procedures</li>
                <li><strong>Legal Considerations:</strong> Some features may violate WhatsApp terms of service</li>
            </ul>

            <h2>Compatibility and Requirements</h2>

            <p>System requirements for multi-account functionality:</p>
            <ul>
                <li><strong>Android Version:</strong> Requires Android 5.0 or higher for best performance</li>
                <li><strong>Storage Space:</strong> Each account requires approximately 500MB</li>
                <li><strong>RAM:</strong> Recommended 3GB RAM for smooth multi-account use</li>
                <li><strong>Processor:</strong> Modern processor recommended for optimal performance</li>
            </ul>

            <h2>Operation Guide</h2>

            <p>Step-by-step guide to setting up multiple accounts:</p>
            <ol>
                <li>Open GB WhatsApp and go to Settings</li>
                <li>Tap on "Multi-Account" or "Account Management"</li>
                <li>Select "Add Account" or "Link Account"</li>
                <li>Enter the phone number for the new account</li>
                <li>Verify the number via SMS or voice call</li>
                <li>Customize settings for the new account</li>
                <li>Set up notification preferences</li>
            </ol>

            <h2>Common Issues and Solutions</h2>

            <p>Troubleshooting multi-account problems:</p>
            <ul>
                <li><strong>Account Conflicts:</strong> Ensure different phone numbers are used</li>
                <li><strong>Notification Delays:</strong> Check battery optimization settings</li>
                <li><strong>Storage Issues:</strong> Regularly clean up unused account data</li>
                <li><strong>Sync Problems:</strong> Check internet connectivity for all accounts</li>
            </ul>

            <h2>Related Topics</h2>

            <p>For more information, explore these related guides:</p>
            <ul>
                <li><a href="/features/">GB WhatsApp Features Overview</a> - Complete feature guide</li>
                <li><a href="/use-cases/business/">Business Use Cases</a> - Using GB WhatsApp for business</li>
            </ul>
        </div>
    </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
    name: 'FeaturesMultiAccount',
    components: { HeadNav },
};
</script>

<style scoped>
.markdown-content {
    padding: 20px;
    line-height: 1.6;
    margin: 0 auto;
    max-width: 1000px;
    background: #ffffff;
    border-radius: 8px;
}

.markdown-content h1 {
    font-size: 2em;
    margin: 0.67em 0;
    border-bottom: 2px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    text-align: center;
}

.markdown-content h2 {
    font-size: 1.5em;
    margin: 0.75em 0;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    margin-top: 40px;
}

.markdown-content h3 {
    font-size: 1.25em;
    margin: 0.83em 0;
    color: #2d3748;
    margin-top: 30px;
}

.markdown-content p {
    margin: 1em 0;
}

.markdown-content ul,
.markdown-content ol {
    margin: 1em 0;
    padding-left: 2em;
}

.markdown-content li {
    margin: 0.5em 0;
}

.markdown-content img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1em 0;
}

.markdown-content a {
    color: #667eea;
    text-decoration: none;
}

.markdown-content a:hover {
    text-decoration: underline;
}

.markdown-content strong {
    font-weight: 700;
}
</style>
